import React, { useState, useEffect } from 'react';
import { Clock, MapPin } from 'lucide-react';

const IndiaLiveClock = () => {
  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    // Update time every second
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    // Cleanup interval on component unmount
    return () => clearInterval(timer);
  }, []);

  // Format time for India (IST - UTC+5:30)
  const formatIndiaTime = (date) => {
    const options = {
      timeZone: 'Asia/Kolkata',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true
    };
    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  // Format date for India
  const formatIndiaDate = (date) => {
    const options = {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      weekday: 'short'
    };
    return new Intl.DateTimeFormat('en-IN', options).format(date);
  };

  const getTimeComponents = (date) => {
    const timeStr = formatIndiaTime(date);
    const [time, period] = timeStr.split(' ');
    const [hours, minutes, seconds] = time.split(':');
    return { hours, minutes, seconds, period: period || 'AM' };
  };

  const { hours, minutes, seconds, period } = getTimeComponents(currentTime);

  return (
    <div className="relative">
      {/* Mini 3D Clock Container */}
      <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-lg shadow-md overflow-hidden"
           style={{
             boxShadow: '0 4px 12px rgba(0, 0, 0, 0.25), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
           }}>
        
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600"></div>
        </div>

        {/* Main Content */}
        <div className="relative px-2 py-1.5">
          {/* Mini Digital Clock Display */}
          <div className="flex items-center gap-0.5">
            {/* Hours */}
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded px-1 py-0.5 border border-slate-600"
                 style={{ boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.3)' }}>
              <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-b from-blue-300 to-blue-500 tabular-nums">
                {hours}
              </span>
            </div>

            {/* Separator */}
            <span className="text-sm font-bold text-blue-400 animate-pulse">:</span>

            {/* Minutes */}
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded px-1 py-0.5 border border-slate-600"
                 style={{ boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.3)' }}>
              <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-b from-purple-300 to-purple-500 tabular-nums">
                {minutes}
              </span>
            </div>

            {/* Separator */}
            <span className="text-sm font-bold text-purple-400 animate-pulse">:</span>

            {/* Seconds */}
            <div className="bg-gradient-to-br from-slate-700 to-slate-800 rounded px-1 py-0.5 border border-slate-600"
                 style={{ boxShadow: 'inset 0 1px 2px rgba(0, 0, 0, 0.3)' }}>
              <span className="text-sm font-bold text-transparent bg-clip-text bg-gradient-to-b from-pink-300 to-pink-500 tabular-nums">
                {seconds}
              </span>
            </div>

            {/* Mini AM/PM */}
            <div className="ml-0.5 bg-gradient-to-br from-slate-700 to-slate-800 rounded px-1 py-0.5 border border-slate-600">
              <span className={`text-[10px] font-bold ${period === 'AM' ? 'text-emerald-400' : 'text-amber-400'}`}>
                {period}
              </span>
            </div>
          </div>

          {/* Mini Date Display */}
          <div className="mt-1 pt-1 border-t border-slate-700/50">
            <div className="flex items-center justify-between text-[9px] text-slate-400">
              <Clock className="w-2.5 h-2.5 text-blue-400" />
              <span className="font-medium">{formatIndiaDate(currentTime)}</span>
              <span className="text-emerald-400 font-semibold">IST</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndiaLiveClock;
